import WorkOrderService from "../../../services/workorder.service";
import TaskRecurrenceRuleService from "../../../services/taskRecurrenceRule.service";
import { verifyRuleGeneration } from "./verifyRuleGeneration";

const dateValue = (value) => value ? String(value).slice(0, 10) : "";
export const removablePropertyOccurrence = (task, ruleId, rentalIds, effectiveDate) =>
  Number(task.task_recurrence_rule_id) === Number(ruleId) &&
  rentalIds.includes(Number(task.rental_id)) && task.status === "new" &&
  !task.deleted && !task.recurrence_override && !task.started_at && !task.completed_at &&
  dateValue(task.scheduled_date) >= effectiveDate;

// The list API cannot filter by rule ID; page through removed homes and check
// each row. Collect before deleting to avoid skipping rows as offsets change.
async function removedOccurrences(ruleId, rentalIds, effectiveDate) {
  const tasks = [];
  for (const rentalId of rentalIds) {
    for (let page = 0; ; page += 1) {
      const response = await WorkOrderService.getWorkOrders(undefined, {
        property: rentalId, date_from: effectiveDate, filters: "new", per: 200, page, sort: "id",
      });
      const body = response.data;
      const rows = typeof body.paginated === "string" ? JSON.parse(body.paginated) : body.paginated;
      if (!Array.isArray(rows)) throw new Error("Unable to verify tasks for removed properties. Try again before saving.");
      tasks.push(...rows.filter((task) => removablePropertyOccurrence(task, ruleId, rentalIds, effectiveDate)));
      if (rows.length < 200 || (page + 1) * 200 >= Number(body.total)) break;
    }
  }
  return tasks;
}

// Progress survives failed cleanup and split follow-up requests in the open
// form. The split endpoint currently copies the old rental links, so explicitly
// update the returned rule before generating its occurrences.
export async function savePropertyRule({ ruleId, payload, scope, effectiveDate,
  previousRentalIds, progress, onRuleId = () => {} }) {
  const nextIds = payload.rental_scope === "all_rentals" ? previousRentalIds : payload.rental_ids || [];
  const removedIds = previousRentalIds.filter((id) => !nextIds.includes(Number(id)));
  const cleanup = scope === "all" ? await removedOccurrences(ruleId, removedIds, effectiveDate) : [];
  let targetId = progress.ruleId || ruleId;
  if (scope === "future" && !progress.ruleId) {
    if (progress.splitUncertain) throw new Error("The previous split result is unknown. Reopen and check the recurring task before continuing.");
    let response;
    try { response = await TaskRecurrenceRuleService.split(ruleId, { effective_date: effectiveDate, payload }); }
    catch (error) {
      if (!error?.response || error.response.status >= 500) progress.splitUncertain = true;
      throw error;
    }
    targetId = response?.data?.new_rule?.id;
    if (!targetId) {
      progress.splitUncertain = true;
      throw new Error("Could not confirm the new recurrence rule. Reopen the task before continuing.");
    }
    progress.ruleId = targetId;
    onRuleId(targetId);
  }
  const response = await TaskRecurrenceRuleService.update(targetId, {
    ...payload, ...(scope === "future" ? { start_date: effectiveDate } : {}), edit_mode: "all",
  });
  if (!progress.deleted) progress.deleted = new Set();
  for (const id of response?.data?.sync?.disposed_ids || []) progress.deleted.add(String(id));
  for (const task of cleanup) {
    if (progress.deleted?.has(String(task.id))) continue;
    // Refresh before cleanup: work that has since started or been edited must survive.
    const response = await WorkOrderService.getWorkOrder(task.id).catch((error) => {
      if (error?.response?.status === 404) return null;
      throw error;
    });
    if (response?.data && removablePropertyOccurrence(response.data, ruleId, removedIds, effectiveDate)) {
      await WorkOrderService.deleteWorkOrder(task.id, { delete_scope: "this" });
    }
    progress.deleted.add(String(task.id));
  }
  const generation = await verifyRuleGeneration(targetId, effectiveDate);
  return { ruleId: targetId, ...generation };
}
